import Axios from "axios";
import { upsertPOSKOT } from "../../services";
import useAuthStore from "../../stores/useAuthStore";
import usePOSConfigStore from "../../stores/usePOSConfigStore";
import useRestaurantStore from "../../stores/useRestaurantStore";

const printToken = (vendor, receiptData) => {
  const tillData = useAuthStore.getState().tillData;
  const tillaccess = JSON.parse(tillData?.tillAccess?.userAccessController || "{}");
  const tillLayout = parseInt(tillaccess?.layout === null || undefined ? 1 : tillaccess?.layout);
  const isTakeAway = useRestaurantStore.getState().dineIn === "N";
  const posConfig = usePOSConfigStore.getState().posConfig;
  const printerURL = tillData.tillAccess.cwrTill.hardwareController.imageUrl;
  let tokenTemplate = tillData.tillAccess.cwrTill.kotPrintTemplate.xmlcode2 ? JSON.parse(tillData.tillAccess.cwrTill.kotPrintTemplate.xmlcode2) : {};
  const processedToken = processTokenTemplate(tokenTemplate, {
    ...receiptData,
    productionCenter: vendor.productionCenter,
    token: vendor.token,
    items: vendor.items,
    printerId:vendor.printerId
  });
  const effectivePrinter = (vendor?.printerId || "").trim();

  let payloadToSend = processedToken;

  if (effectivePrinter) {
    payloadToSend = {
      printer: effectivePrinter,
      ...processedToken
    };
  } else {
    payloadToSend = processedToken;
  }

  const fireUpsertPOSKOT = async () => {
    if (posConfig?.enableKOTPrint === "Y" && (tillLayout !== 2 || (tillLayout === 2 && isTakeAway))) {
      const kotResult = await upsertPOSKOT({ ...receiptData, vendors: [vendor] }, tillData);
      if (kotResult.status !== "Success") {
        console.error("KOT insertion failed:", kotResult.message);
      }
    }
  };

  const decodedData = JSON.stringify(payloadToSend, null, 2);
  // Till template flag "Require KOT Print" (posConfig.requireKOTPrint):
  // "Y" decouples KOT persistence from the physical print job — fired in parallel below,
  // unaffected by whether the print POST succeeds or fails (a slow/unreachable printer, up to
  // the 60s global axios timeout in App.js, must never delay the order showing up on KDS).
  // Unset/"N" preserves the original behavior: KOT is only persisted once print succeeds.
  if (posConfig?.requireKOTPrint === "Y") {
    fireUpsertPOSKOT();
  }
  Axios.post(`${printerURL}printer`, decodedData, {
    headers: {
      "Content-Type": "text/plain",
    },
  })
    .then(async () => {
      if (posConfig?.requireKOTPrint !== "Y") {
        await fireUpsertPOSKOT();
      }
    })
    .catch((error) => {
      console.error(`Failed to print token for ${vendor.productionCenter}:`, error);
    });
};

const replacePlaceholders = (text, data) => {
  return text.replace(/{{([^}]+)}}/g, (match, placeholder) => {
    if (placeholder === "currentDateTime") {
      return new Date().toLocaleString();
    }
    let value = getDeepValue(data, placeholder);
    if (typeof value === "number") {
      return value === 0 ? "0.00" : value.toFixed(2);
    }
    if (placeholder.includes("+") || placeholder.includes("-") || placeholder.includes("*") || placeholder.includes("/")) {
      try {
        const resolvedExpression = placeholder.replace(/\b\w+\b/g, (varName) => {
          const val = getDeepValue(data, varName);
          return typeof val === "number" ? val : 0;
        });
        const result = eval(resolvedExpression);
        return result === 0 ? "0.00" : result.toFixed(2);
      } catch (error) {
        console.error("Error evaluating expression:", placeholder, error);
        return "0.00";
      }
    }
    return value !== null && value !== undefined ? value : "";
  });
};

const getDeepValue = (obj, path) => {
  return path.split(".").reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : null), obj);
};

const formatValue = (value, format) => {
  if (format === "currency" && typeof value === "number") {
    return value.toFixed(2);
  }
  return value;
};

const evaluateCondition = (condition, data) => {
  try {
    const resolvedCondition = condition.replace(/{{([^}]+)}}/g, (match, placeholder) => {
      try {
        let value = getDeepValue(data, placeholder);
        if (value === null || value === undefined) {
          return "undefined";
        }
        if (typeof value === "string") {
          try {
            value = JSON.parse(value);
          } catch (e) {}
        }
        if (typeof value === "string") {
          return `'${value}'`;
        }
        if (typeof value === "boolean" || typeof value === "number") {
          return value;
        }
        return value.toString();
      } catch (error) {
        console.error("Error resolving placeholder:", placeholder, error);
        return "undefined";
      }
    });
    return eval(resolvedCondition);
  } catch (error) {
    console.error("Error evaluating condition:", condition, error);
    return false;
  }
};

const processTokenTemplate = (template, data) => {
  const processedTemplate = JSON.parse(JSON.stringify(template));
  processedTemplate.elements = processedTemplate.elements.flatMap((element) => {
    if (element.type === "conditional" && element.condition && element.elements) {
      const conditionResult = evaluateCondition(element.condition, data);
      if (conditionResult) {
        return element.elements.flatMap((nestedElement) => processTokenTemplate({ elements: [nestedElement] }, data).elements);
      }
      return [];
    }
    if (element.type === "text" && element.content && typeof element.content === "string") {
      element.content = replacePlaceholders(element.content, data);
    }
    if (element.type === "loop" && element.data && typeof element.data === "string") {
      const loopDataPath = element.data.replace(/{{|}}/g, "");
      const loopData = getDeepValue(data, loopDataPath) || [];
      if (Array.isArray(loopData)) {
        return loopData.flatMap((loopItem) => {
          return element.elements.flatMap((loopElement) => {
            const newElement = JSON.parse(JSON.stringify(loopElement));
            if (newElement.type === "text" && newElement.content && typeof newElement.content === "string") {
              newElement.content = replacePlaceholders(newElement.content, loopItem);
            }
            if (newElement.type === "extraRows" && newElement.rows) {
              const extraElements = newElement.rows.flatMap((row) => {
                if (row.field && row.loop) {
                  const loopData = getDeepValue(loopItem, row.field) || [];
                  if (!Array.isArray(loopData)) {
                    return [];
                  }
                  return loopData.flatMap((subItem) => {
                    const subValue = subItem[row.subField];
                    const shouldHide = row.hideIfZeroOrEmpty && (subValue === null || subValue === undefined || subValue === "");
                    if (!shouldHide && subValue !== null && subValue !== undefined) {
                      return [
                        {
                          type: "text",
                          content: (row.prefix || "") + subValue,
                          align: row.align || "left",
                          style: row.style || ["normal"],
                          length: row.length || 30,
                          startPosition: row.startPosition || 0,
                        },
                      ];
                    }
                    return [];
                  });
                }
                const value = getDeepValue(loopItem, row.field);
                const shouldHide = row.hideIfZeroOrEmpty && (value === null || value === undefined || value === 0);
                if (!shouldHide && value !== null && value !== undefined) {
                  return [
                    {
                      type: "text",
                      content: (row.prefix || "") + value,
                      align: row.align || "left",
                      style: row.style || ["normal"],
                      length: row.length || 30,
                      startPosition: row.startPosition || 0,
                    },
                  ];
                }
                return [];
              });
              return extraElements;
            }
            return newElement;
          });
        });
      }
      return [];
    }
    if (element.type === "table" && element.data && typeof element.data === "string" && element.dataMapping) {
      const dataPath = element.data.replace(/{{|}}/g, "");
      const tableData = getDeepValue(data, dataPath) || [];
      const columnKeys = element.columns.map((col, idx) => ({
        key: Object.keys(element.dataMapping).find((k) => element.dataMapping[k] === Object.values(element.dataMapping)[idx]),
        width: col.width,
        alignment: col.alignment,
        format: col.format,
      }));
      element.data = tableData
        .flatMap((item) => {
          const mainRow = {};
          for (const targetKey in element.dataMapping) {
            mainRow[targetKey] = formatValue(item[element.dataMapping[targetKey]], columnKeys.find((ck) => ck.key === targetKey)?.format);
          }
          const extraRows = (element.extraRows || []).flatMap((extra) => {
            if (extra.fields && Array.isArray(extra.fields)) {
              const allHidden = extra.fields.every((fieldConfig) => {
                const value = item[fieldConfig.field];
                return fieldConfig.hideIfZeroOrEmpty && (value === null || value === undefined || value === 0);
              });
              if (allHidden) {
                return [];
              }
              const extraRow = {};
              let hasContent = false;
              columnKeys.forEach((col) => {
                const fieldConfig = extra.fields.find((f) => f.column === col.key);
                if (fieldConfig) {
                  const value = item[fieldConfig.field];
                  const shouldHide = fieldConfig.hideIfZeroOrEmpty && (value === null || value === undefined || value === 0);
                  if (!shouldHide && value !== null && value !== undefined) {
                    extraRow[col.key] = (fieldConfig.prefix || "") + (fieldConfig.format ? formatValue(value, fieldConfig.format) : value);
                    hasContent = true;
                  } else {
                    extraRow[col.key] = "";
                  }
                } else {
                  extraRow[col.key] = "";
                }
              });
              return hasContent ? [extraRow] : [];
            }
            if (extra.field && extra.loop) {
              const loopData = item[extra.field] || [];
              if (!Array.isArray(loopData)) {
                return [];
              }
              return loopData.flatMap((loopItem) => {
                const subValue = loopItem[extra.subField];
                const shouldHide = extra.hideIfZeroOrEmpty && (subValue === null || subValue === undefined || subValue === "");
                if (!shouldHide && subValue !== null && subValue !== undefined) {
                  if (extra.column) {
                    const extraRow = {};
                    columnKeys.forEach((col) => {
                      extraRow[col.key] = col.key === extra.column ? (extra.prefix || "") + subValue : "";
                      if (extra.format && col.key === extra.column) {
                        extraRow[col.key] = formatValue(subValue, extra.format);
                      }
                    });
                    return [extraRow];
                  } else {
                    return [
                      {
                        type: "text",
                        content: (extra.prefix || "") + subValue,
                        align: extra.align || "left",
                        style: extra.style || ["normal"],
                        length: extra.length || 30,
                        startPosition: extra.startPosition || 0,
                      },
                    ];
                  }
                }
                return [];
              });
            }
            const value = item[extra.field];
            const shouldHide = extra.hideIfZeroOrEmpty && (value === null || value === undefined || value === 0);
            if (!shouldHide && value !== null && value !== undefined) {
              if (extra.column) {
                const extraRow = {};
                columnKeys.forEach((col) => {
                  extraRow[col.key] = col.key === extra.column ? value : "";
                  if (extra.format && col.key === extra.column) {
                    extraRow[col.key] = formatValue(value, extra.format);
                  }
                });
                return [extraRow];
              } else {
                return [
                  {
                    type: "text",
                    content: (extra.prefix || "") + value,
                    align: extra.align || "left",
                    style: extra.style || ["normal"],
                    length: extra.length || 30,
                    startPosition: extra.startPosition || 0,
                  },
                ];
              }
            }
            return [];
          });
          return [mainRow, ...extraRows];
        })
        .filter((row) => {
          if (row.type === "text") {
            return true;
          }
          return Object.values(row).some((val) => val !== "" && val !== null && val !== undefined);
        });
    }
    if (element.type === "barcode" && element.content) {
      element.content = replacePlaceholders(element.content, data);
    }
    if (element.type === "qrcode" && element.content) {
      element.content = replacePlaceholders(element.content, data);
    }
    if (element.type === "spacing" || element.type === "line") {
      return element;
    }
    return element;
  });
  return processedTemplate;
};

export default printToken;
